/**
 * React Hook for Retry Handling
 * Provides retry state management and user-friendly retry interfaces
 * 
 * Requirements: 5.3, 5.1
 */

import React, { useState, useCallback, useRef } from 'react';
import { 
  RetryManager,
  retryNetworkRequest,
  retryPaymentOperation,
  retryAccountCreation,
  retryTwilioOperation,
  retryAuthOperation
} from '@/utils/retry-mechanisms';
import { 
  handlePaymentError,
  handleAccountCreationError,
  handleTwilioError,
  handleAuthError
} from '@/utils/error-handling';
import { trackEvent } from '@/utils/analytics';

/**
 * Hook for managing retry operations with user feedback
 * @param {string} operationType - Type of operation (network, payment, account_creation, etc.)
 * @param {Object} options - Hook options
 * @returns {Object} Retry state and functions
 */
export const useRetryHandler = (operationType = 'network', options = {}) => {
  const {
    onSuccess,
    onError,
    onRetryAttempt,
    maxUserRetries = 3,
    showRetryUI = true
  } = options;

  const [retryState, setRetryState] = useState({
    isRetrying: false,
    attemptCount: 0,
    lastError: null,
    canRetry: true,
    retryHistory: []
  });

  const retryManagerRef = useRef(new RetryManager(operationType));
  const operationRef = useRef(null);
  const contextRef = useRef({});

  /**
   * Execute operation with automatic retry
   * @param {Function} operation - Operation to execute
   * @param {Object} context - Operation context
   * @returns {Promise} Operation result
   */
  const executeWithRetry = useCallback(async (operation, context = {}) => {
    operationRef.current = operation;
    contextRef.current = context;

    setRetryState(prev => ({
      ...prev,
      isRetrying: true,
      lastError: null
    }));

    try {
      const result = await retryManagerRef.current.executeWithRetry(operation, context);
      
      setRetryState(prev => ({
        ...prev,
        isRetrying: false,
        attemptCount: 0,
        lastError: null,
        canRetry: true
      }));

      onSuccess?.(result);
      return result;

    } catch (error) {
      const processedError = processError(error, operationType, context);
      
      setRetryState(prev => ({
        ...prev,
        isRetrying: false,
        lastError: processedError,
        canRetry: processedError.canRetry && prev.attemptCount < maxUserRetries,
        retryHistory: [...prev.retryHistory, {
          timestamp: new Date().toISOString(),
          error: processedError,
          context
        }]
      }));

      onError?.(processedError);
      throw processedError;
    }
  }, [operationType, onSuccess, onError, maxUserRetries]);

  /**
   * Execute operation with manual retry capability
   * @param {Function} operation - Operation to execute
   * @param {Object} context - Operation context
   * @returns {Promise} Operation result with retry info
   */
  const executeWithManualRetry = useCallback(async (operation, context = {}) => {
    operationRef.current = operation;
    contextRef.current = context;

    setRetryState(prev => ({
      ...prev,
      isRetrying: true,
      lastError: null
    }));

    try {
      const result = await retryManagerRef.current.executeWithManualRetry(operation, context);
      
      if (result.success) {
        setRetryState(prev => ({
          ...prev,
          isRetrying: false,
          attemptCount: 0,
          lastError: null,
          canRetry: true
        }));

        onSuccess?.(result.result);
        return result;
      } else {
        const processedError = processError(result.error, operationType, context);
        
        setRetryState(prev => ({
          ...prev,
          isRetrying: false,
          lastError: processedError,
          canRetry: result.canRetry && prev.attemptCount < maxUserRetries,
          retryHistory: [...prev.retryHistory, {
            timestamp: new Date().toISOString(),
            error: processedError,
            context
          }]
        }));

        onError?.(processedError);
        return result;
      }

    } catch (error) {
      const processedError = processError(error, operationType, context);
      
      setRetryState(prev => ({
        ...prev,
        isRetrying: false,
        lastError: processedError,
        canRetry: false
      }));

      onError?.(processedError);
      throw processedError;
    }
  }, [operationType, onSuccess, onError, maxUserRetries]);

  /**
   * Manual retry function for user-initiated retries
   */
  const manualRetry = useCallback(async () => {
    if (!operationRef.current || !retryState.canRetry) {
      return;
    }

    const newAttemptCount = retryState.attemptCount + 1;
    
    setRetryState(prev => ({
      ...prev,
      isRetrying: true,
      attemptCount: newAttemptCount,
      lastError: null
    }));

    // Track manual retry attempt
    trackEvent('manual_retry_attempt', {
      operation_type: operationType,
      attempt_number: newAttemptCount,
      previous_error: retryState.lastError?.category,
      context: contextRef.current
    });

    onRetryAttempt?.(newAttemptCount);

    try {
      const result = await operationRef.current();
      
      setRetryState(prev => ({
        ...prev,
        isRetrying: false,
        lastError: null,
        canRetry: true
      }));

      // Track successful manual retry
      trackEvent('manual_retry_success', {
        operation_type: operationType,
        attempt_number: newAttemptCount,
        context: contextRef.current
      });

      onSuccess?.(result);
      return result;

    } catch (error) {
      const processedError = processError(error, operationType, contextRef.current);
      
      setRetryState(prev => ({
        ...prev,
        isRetrying: false,
        lastError: processedError,
        canRetry: processedError.canRetry && newAttemptCount < maxUserRetries
      }));

      // Track failed manual retry
      trackEvent('manual_retry_failed', {
        operation_type: operationType,
        attempt_number: newAttemptCount,
        error_category: processedError.category,
        context: contextRef.current
      });

      onError?.(processedError);
      throw processedError;
    }
  }, [retryState.canRetry, retryState.attemptCount, operationType, onSuccess, onError, onRetryAttempt, maxUserRetries]);

  /**
   * Reset retry state
   */
  const resetRetryState = useCallback(() => {
    setRetryState({
      isRetrying: false,
      attemptCount: 0,
      lastError: null,
      canRetry: true,
      retryHistory: []
    });
    operationRef.current = null;
    contextRef.current = {};
  }, []);

  /**
   * Get retry button props for UI components
   */
  const getRetryButtonProps = useCallback(() => ({
    disabled: !retryState.canRetry || retryState.isRetrying,
    loading: retryState.isRetrying,
    onClick: manualRetry,
    children: retryState.isRetrying 
      ? 'Retrying...' 
      : `Retry${retryState.attemptCount > 0 ? ` (${retryState.attemptCount}/${maxUserRetries})` : ''}`
  }), [retryState, manualRetry, maxUserRetries]);

  return {
    // State
    ...retryState,
    
    // Functions
    executeWithRetry,
    executeWithManualRetry,
    manualRetry,
    resetRetryState,
    
    // UI helpers
    getRetryButtonProps,
    
    // Computed properties
    hasReachedMaxRetries: retryState.attemptCount >= maxUserRetries,
    retryProgress: Math.min((retryState.attemptCount / maxUserRetries) * 100, 100)
  };
};

/**
 * Specialized hooks for different operation types
 */

/**
 * Hook for payment retry operations
 */
export const usePaymentRetry = (options = {}) => {
  return useRetryHandler('payment', {
    ...options,
    maxUserRetries: options.maxUserRetries || 2
  });
};

/**
 * Hook for account creation retry operations
 */
export const useAccountCreationRetry = (options = {}) => {
  return useRetryHandler('account_creation', {
    ...options,
    maxUserRetries: options.maxUserRetries || 3
  });
};

/**
 * Hook for Twilio retry operations
 */
export const useTwilioRetry = (options = {}) => {
  return useRetryHandler('twilio', {
    ...options,
    maxUserRetries: options.maxUserRetries || 2
  });
};

/**
 * Hook for network retry operations
 */
export const useNetworkRetry = (options = {}) => {
  return useRetryHandler('network', {
    ...options,
    maxUserRetries: options.maxUserRetries || 3
  });
};

/**
 * Hook for authentication retry operations
 */
export const useAuthRetry = (options = {}) => {
  return useRetryHandler('auth', {
    ...options,
    maxUserRetries: options.maxUserRetries || 2
  });
};

/**
 * Process error based on operation type
 * @param {Error} error - Error to process
 * @param {string} operationType - Operation type
 * @param {Object} context - Error context
 * @returns {Object} Processed error
 */
function processError(error, operationType, context) {
  switch (operationType) {
    case 'payment':
      return handlePaymentError(error, context);
    case 'account_creation':
      return handleAccountCreationError(error, context);
    case 'twilio':
      return handleTwilioError(error, context);
    case 'auth':
      return handleAuthError(error, context);
    default:
      // Generic error handling for network and other operations
      return {
        id: `ERR-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
        category: 'network_error',
        severity: 'medium',
        userMessage: {
          title: 'Connection Error',
          message: 'Please check your internet connection and try again.'
        },
        canRetry: true,
        originalError: error,
        timestamp: new Date().toISOString()
      };
  }
}

/**
 * Higher-order component for adding retry functionality to components
 * @param {React.Component} WrappedComponent - Component to wrap
 * @param {string} operationType - Operation type
 * @param {Object} options - Retry options
 * @returns {React.Component} Enhanced component with retry functionality
 */
export const withRetryHandler = (WrappedComponent, operationType, options = {}) => {
  return function RetryEnhancedComponent(props) {
    const retryHandler = useRetryHandler(operationType, options);
    
    return (
      <WrappedComponent
        {...props}
        retryHandler={retryHandler}
      />
    );
  };
};

/**
 * Utility functions for common retry patterns
 */

/**
 * Retry a fetch request with network retry logic
 * @param {string} url - URL to fetch
 * @param {Object} options - Fetch options
 * @param {Object} context - Request context
 * @returns {Promise} Fetch response
 */
export const retryFetch = async (url, options = {}, context = {}) => {
  return retryNetworkRequest(async () => {
    const response = await fetch(url, options);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    return response;
  }, {
    url,
    method: options.method || 'GET',
    ...context
  });
};

/**
 * Retry a payment operation with payment-specific logic
 * @param {Function} paymentFunction - Payment function to retry
 * @param {Object} paymentContext - Payment context
 * @returns {Promise} Payment result
 */
export const retryPayment = async (paymentFunction, paymentContext = {}) => {
  return retryPaymentOperation(paymentFunction, paymentContext);
};

/**
 * Retry account creation with fallback to background processing
 * @param {Function} accountFunction - Account creation function
 * @param {Object} accountContext - Account context
 * @returns {Promise} Account creation result
 */
export const retryAccountSetup = async (accountFunction, accountContext = {}) => {
  return retryAccountCreation(accountFunction, accountContext);
};